import { useEffect } from "react";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { useStore } from "utils/useStore";
import type { NumericRangeDraft } from "../../../Grouping/groupByDraft";

export type ModuleScoreModuleOption = {
    id: string;
    label: string;
};

export type ModuleScoreGroupingOptions = {
    /** Modules the viewer may group scores by. */
    moduleOptions: ModuleScoreModuleOption[];
    modulesLoading: boolean;
};

/**
 * Sources the module list a Module Score grouping is configured from.
 *
 * Reads the same store the module score *filter* does, narrowed by `moduleScoreFilterableModules`
 * so a chart cannot be grouped by a module the viewer may not see: Restrict Evaluations hides every
 * module for a non-ultra user, and a restricted category needs its own permission. That mirrors
 * `useQuestionResultGroupingOptions`, which reads the already-narrowed
 * `questionResultFilterableModules`. Values are read during render rather than memoized so MobX
 * tracks them.
 *
 * Returns an empty list for any other range dimension, so callers can use it unconditionally.
 */
export const useModuleScoreGroupingOptions = (
    draft: NumericRangeDraft,
): ModuleScoreGroupingOptions => {
    const filterValuesStore = useStore(ApplicationFilterValuesStore);

    const isModuleScore = draft.option === "ModuleScore";

    useEffect(() => {
        if (!isModuleScore) return;
        void filterValuesStore.loadHierarchyModules();
    }, [filterValuesStore, isModuleScore]);

    const moduleOptions: ModuleScoreModuleOption[] = isModuleScore
        ? filterValuesStore.moduleScoreFilterableModules.map((module) => ({
              id: module.id,
              label: module.title,
          }))
        : [];

    return {
        moduleOptions,
        modulesLoading:
            isModuleScore &&
            filterValuesStore.getTaskLoading(
                ApplicationFilterValuesStore.Tasks
                    .LOAD_LICENSED_HIERARCHY_MODULES,
            ),
    };
};
